/**
 * Разбор ответов об ошибках и события, которыми на них реагирует кабинет.
 *
 * Коды бэк отдаёт полем `code`, а не только текстом: по нему клиент решает,
 * куда вести человека. Три из них — не ошибки, а развилки:
 *
 * - `partner_not_found` (404, вход) — кабинета нет, предлагаем регистрацию;
 * - `already_registered` (409, регистрация) — кабинет есть, предлагаем вход;
 * - `registration_incomplete` (409, любой маршрут кабинета) — номер
 *   подтверждён, анкета не отправлена: молча возвращаем в анкету.
 *
 * Разбирать 409 в каждом из двух десятков методов бессмысленно — ответ один
 * и тот же, и реакция на него одна. Поэтому гейт живёт в интерцепторе, а сюда
 * вынесен, чтобы его можно было проверить тестом, не поднимая axios.
 */

/** Кабинета с таким номером нет — вход невозможен. */
export const CODE_PARTNER_NOT_FOUND = 'partner_not_found';

/** У номера уже есть кабинет — регистрация невозможна. */
export const CODE_ALREADY_REGISTERED = 'already_registered';

/** Номер подтверждён, анкета не отправлена. */
export const CODE_REGISTRATION_INCOMPLETE = 'registration_incomplete';

export const EVENT_UNAUTHORIZED = 'unauthorized';
export const EVENT_FORBIDDEN = 'forbidden';
export const EVENT_SUBSCRIPTION_EXPIRED = 'subscription-expired';
export const EVENT_REGISTRATION_INCOMPLETE = 'registration-incomplete';

/** Ответ об ошибке в том виде, в каком его отдаёт бэк. */
interface ApiErrorBody {
  message?: string;
  code?: string;
  expires_at?: string | null;
  errors?: Record<string, string[]>;
}

interface ApiErrorLike {
  response?: {
    status?: number;
    data?: ApiErrorBody;
  };
  message?: string;
}

export function apiErrorStatus(error: unknown): number | null {
  return (error as ApiErrorLike)?.response?.status ?? null;
}

/** Машинный код ответа — по нему клиент выбирает дверь, а не по тексту. */
export function apiErrorCode(error: unknown): string | null {
  return (error as ApiErrorLike)?.response?.data?.code ?? null;
}

/** Текст для человека: сообщение сервера, потом текст исключения, потом запасной. */
export function apiErrorMessage(error: unknown, fallback = 'Произошла ошибка'): string {
  const typed = error as ApiErrorLike;
  return typed?.response?.data?.message || typed?.message || fallback;
}

/**
 * Ошибки валидации в плоском виде: `legal.registration_number` → текст.
 *
 * Laravel отдаёт массив сообщений на поле — показываем первое: второе почти
 * всегда следствие первого, а два текста под одним полем читаются как сбой.
 */
export function apiFieldErrors(error: unknown): Record<string, string> {
  const errors = (error as ApiErrorLike)?.response?.data?.errors;
  if (!errors) return {};

  return Object.entries(errors).reduce<Record<string, string>>((acc, [field, messages]) => {
    const first = Array.isArray(messages) ? messages[0] : messages;
    if (typeof first === 'string') acc[field] = first;
    return acc;
  }, {});
}

/** Данные сессии кабинета. Регистрация продолжаться без токена не может. */
export function clearStoredSession(): void {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('partner');
  localStorage.removeItem('registrationPhone');
}

/**
 * Превратить ответ об ошибке в событие для React-слоя.
 *
 * Событие только сообщает о факте; куда вести — решает `components/App`.
 * Ошибка после этого возвращается вызывающему коду без изменений: он должен
 * увидеть свою ошибку, а не молчаливый успех.
 */
export function dispatchApiErrorEvent(error: unknown): void {
  const status = apiErrorStatus(error);
  const code = apiErrorCode(error);

  if (status === 401) {
    clearStoredSession();
    window.dispatchEvent(new CustomEvent(EVENT_UNAUTHORIZED));
    return;
  }

  if (status === 403) {
    window.dispatchEvent(new CustomEvent(EVENT_FORBIDDEN));
    return;
  }

  if (status === 402 && code === 'subscription_expired') {
    window.dispatchEvent(
      new CustomEvent(EVENT_SUBSCRIPTION_EXPIRED, {
        detail: {
          message: apiErrorMessage(error),
          expires_at: (error as ApiErrorLike)?.response?.data?.expires_at ?? null,
        },
      })
    );
    return;
  }

  // 409 бывает и на регистрации (`already_registered`) — там это ответ на
  // конкретное действие, его разбирает форма. Гейт кабинета реагирует только
  // на незаконченную анкету.
  if (status === 409 && code === CODE_REGISTRATION_INCOMPLETE) {
    window.dispatchEvent(new CustomEvent(EVENT_REGISTRATION_INCOMPLETE));
  }
}
